import { IDexTrade, ITicker } from '@kattana-io/common';
import { useTableData } from './useTableData';
import useTicker from '../../../../hooks/useTicker';
import PairIcons from '../../../../molecules/PairIcons';
import Text from '../../../../atoms/Text';
import { decorateExchangeName } from '../../../../helpers/decorators';
import {
  formatTimestamp,
  formatTx,
} from '../../../../components/DexTrades/formatters';
import { onTxScan } from '../../../../helpers/addressUtils';
import DesktopTable from '../../../../molecules/Table/DesktopTable';
import NumberDisplay from '../../../../molecules/NumberDisplay';

export const LS_WALLET_TRADES_KEY =
  'kattana-simple.wallet_trades_table_columns';

type tickersMap = Record<string, ITicker>;

const buyClassName = 'text--green-600';
const sellClassName = 'text--red-600';
interface IWalletTradesTableProps {
  network: string;
  timezone?: string;
  trades: IDexTrade[];
  tickers: tickersMap;
  loading: boolean;
  width?: number;
  onTickerClick: (ticker: ITicker) => void;
}

/**
 * Render a table
 * @param props
 * @returns
 */
const WalletTradesTable = ({
  tickers,
  trades,
  timezone,
  loading,
  width,
  network,
  onTickerClick,
}: IWalletTradesTableProps) => {
  const { columns, data, tableColumnsWidth } = useTableData({
    trades,
  });

  const renderCell = (cell: any) => {
    const { chain, pair, buy, amount0, amount1, price_b_usd, price_a_usd } =
      cell.row.original as IDexTrade;
    const key = `${chain}-${pair}`;
    const item = tickers[key];
    const ticker = useTicker(item);

    // if pair flipped - buy/sell will be reversed
    const isBuy = buy === 1 ? !item.flipped : item.flipped;

    // get amounts for base and quote considering flipped
    const baseAmount = Number(item.flipped ? amount1 : amount0);
    const quoteAmount = Number(item.flipped ? amount0 : amount1);

    // get in and out amounts depending on trade was buy or sell
    const amountIn = isBuy
      ? quoteAmount / 10 ** ticker.quote_decimals
      : baseAmount / 10 ** ticker.base_decimals;

    const amountOut = isBuy
      ? baseAmount / 10 ** ticker.base_decimals
      : quoteAmount / 10 ** ticker.quote_decimals;

    switch (cell.column.id) {
    case 'ticker':
      return (
        <span className='ticker__cell' onClick={() => onTickerClick(item)}>
          <PairIcons {...{ ...ticker, pair }} />
          <div className='trades__ticker_description'>
            <div className='trades__ticker_description__name'>
              <Text size={12} className='text--grey-200 text--weight-600'>
                {ticker.name}
              </Text>
              <div
                className={`icon-cl__get-to-r ${
                  isBuy ? buyClassName : sellClassName
                }`}
              />
            </div>
            <Text size={12} className='text--grey-600'>
              {decorateExchangeName(ticker.exchange, ticker.chain)}
            </Text>
          </div>
        </span>
      );
    case 'type':
      return (
        <Text size={12} className={isBuy ? buyClassName : sellClassName}>
          {isBuy ? 'Buy' : 'Sell'}
        </Text>
      );
    case 'timestamp':
      return (
        <Text size={12}>
          {formatTimestamp(cell.value, timezone, 'full').split(' ')[0]}
          <br />
          {formatTimestamp(cell.value, timezone, 'full').split(' ')[1]}
        </Text>
      );
    case 'priceUSD':
      return (
        <NumberDisplay
          value={item.flipped ? price_b_usd : price_a_usd}
          endSymbol=' $'
          textProps={{
            size: 12,
            className: isBuy ? buyClassName : sellClassName,
          }}
        />
      );
    case 'amountIn':
      return (
        <NumberDisplay
          value={amountIn}
          endSymbol={` ${isBuy ? ticker.quote : ticker.base}`}
          textProps={{ size: 12, className: 'text--grey-200' }}
        />
      );
    case 'amountOut':
      return (
        <NumberDisplay
          value={amountOut}
          endSymbol={` ${isBuy ? ticker.base : ticker.quote}`}
          textProps={{ size: 12, className: 'text--grey-200' }}
        />
      );
    case 'valueUSD':
      return (
        <NumberDisplay
          value={cell.value}
          endSymbol=' $'
          textProps={{
            size: 12,
            className: isBuy ? buyClassName : sellClassName,
          }}
        />
      );
    case 'tx':
      return (
        <Text
          className='tx'
          size={12}
          onClick={() => onTxScan(cell.row.original.tx, network)}
        >
          {formatTx(cell.value)}
        </Text>
      );
    default:
      return <Text size={12}>{cell.value}</Text>;
    }
  };

  return (
    <DesktopTable
      dataKey={LS_WALLET_TRADES_KEY}
      title='wallet_trades'
      tableColumnsWidth={tableColumnsWidth}
      tableWidth={width || 0}
      columns={columns}
      data={data}
      loading={loading}
      renderCell={renderCell}
      withBorder={false}
      withTableControls={false}
      rememberColWidth={false}
      rowHeight={40}
      hiddenColumns={[]}
    />
  );
};

export default WalletTradesTable;
